import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import { defaults as defaultControls } from 'ol/control';
import { toPng } from 'html-to-image';
import { useCallback, useEffect, useState } from 'react';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  shapeToFeature,
  useOpenLayersContext,
  zoomToFeatureExtent,
} from '@ais-ui/react-openlayers';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { baseColours, strokeColours } from '@ais-ui/basecolours';

export const useMapSnapshot = (
  shape: GeoJson | null | undefined = null,
  shapeColor: string
): { snapshot: string | null; isLoading: boolean } => {
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { displayProjection, dataProjection } = useOpenLayersContext();

  const applyOpacity = (shapeColor: string, alpha: number) => {
    const colorNames = Object.keys(baseColours);

    // add opacity if it recies hex color, CC = 80%, 4D = 30%
    if (shapeColor === 'grey' && shapeColor.charAt(0)) {
      shapeColor += 'CC';
    } else if (shapeColor.charAt(0) === '#') {
      shapeColor += '4D';
    }

    if (!colorNames.includes(shapeColor)) {
      return shapeColor;
    }
    if (shapeColor === 'gray') {
      alpha = 0.8;
    }
    const hslColor = baseColours[shapeColor as keyof typeof baseColours];
    return hslColor.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
  };

  const fillColor = applyOpacity(shapeColor, 0.3);

  const captureMapSnapshot = useCallback(async (): Promise<string | null> => {
    if (!shape) {
      return null;
    }

    const mapContainer = document.createElement('div');
    mapContainer.style.position = 'fixed';
    mapContainer.style.top = '0';
    mapContainer.style.left = '0';
    mapContainer.style.width = '420px';
    mapContainer.style.height = '160px';
    mapContainer.style.overflow = 'hidden';
    mapContainer.style.zIndex = '-1';
    document.body.appendChild(mapContainer);

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '420px';
    overlay.style.height = '160px';
    overlay.style.zIndex = '1';
    document.body.appendChild(overlay);

    const map = new Map({
      target: mapContainer,
      view: new View({
        center: [0, 0],
        zoom: 2,
      }),
      controls: defaultControls({ zoom: false, attribution: false }),
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
    });
    const feature = shapeToFeature(shape, displayProjection, dataProjection);
    if (feature) {
      const style = new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({
            color: fillColor,
          }),
          stroke: new Stroke({
            color: strokeColours[shapeColor as keyof typeof strokeColours],
            width: 1,
          }),
        }),
        stroke: new Stroke({
          color: strokeColours[shapeColor as keyof typeof strokeColours],
          width: 3,
        }),
        fill: new Fill({
          color: fillColor,
        }),
      });

      const vectorSource = new VectorSource({
        features: [feature],
      });
      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: style,
      });
      map.addLayer(vectorLayer);
      zoomToFeatureExtent(feature, map, [5, 5, 5, 5]);
    }

    await new Promise((resolve) => {
      map.once('rendercomplete', resolve);
    });

    const dataUrl = await toPng(mapContainer);
    document.body.removeChild(mapContainer);
    document.body.removeChild(overlay);

    return dataUrl;
  }, [shape, shapeColor, displayProjection, dataProjection]);

  useEffect(() => {
    const generateSnapshot = async () => {
      setIsLoading(true);
      const snapshot = await captureMapSnapshot();
      setSnapshot(snapshot);
      setIsLoading(false);
    };

    generateSnapshot();
  }, [captureMapSnapshot, shape]);

  return { snapshot, isLoading };
};
